import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, KeyRound, Clock, Loader2, Save } from 'lucide-react';
import { URLS } from '../../router-x/routes';
import { useGetUserById, useUpdateTeamMemberPermissions, useUpdateTeamMemberMeetingRoomQuota } from '../../custom-hooks/use-users';
import { NotifyError } from '../../utils/functions';
import { BottomUXSpace } from '../../components';
import { useAuth } from '../../services/contexts/authContext';

const PermissionToggle = ({ title, subtitle, checked, onToggle, disabled }) => (
    <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className="flex w-full items-center justify-between gap-3.5 rounded-[13px] border border-[#F1E9DC] bg-cream p-4 text-left transition-colors hover:border-[#E0C3A4] disabled:opacity-70"
    >
        <div>
            <div className="font-display text-sm font-semibold text-ink">{title}</div>
            <div className="mt-0.5 text-[12.5px] text-[#8C8175]">{subtitle}</div>
        </div>
        <span className={`relative h-[26px] w-[46px] shrink-0 rounded-full transition-colors ${checked ? 'bg-brand' : 'bg-[#D8CDBD]'}`}>
            <span className={`absolute top-0.5 h-[22px] w-[22px] rounded-full bg-white shadow transition-all ${checked ? 'left-[22px]' : 'left-0.5'}`} />
        </span>
    </button>
);

const CustomerTeamMemberDetail = () => {
    const { teamMemberId } = useParams();
    const { userDetails } = useAuth();

    const {
        data: memberData,
        isLoading,
        error,
        refetch
    } = useGetUserById(teamMemberId);

    const updateUserMutation = useUpdateTeamMemberPermissions();
    const updateQuotaMutation = useUpdateTeamMemberMeetingRoomQuota();

    // TM quota allocation state (in hours for display, saved as hours to API which converts to minutes)
    const [isEditingQuota, setIsEditingQuota] = useState(false);
    const [quotaForm, setQuotaForm] = useState(null);

    const handleTogglePermission = async (pageId) => {
        try {
            const currentAllowedPages = memberData?.data?.user?.allowedPages || [];
            let newPages = [...currentAllowedPages];
            if (newPages.includes(pageId)) {
                newPages = newPages.filter(p => p !== pageId);
            } else {
                newPages.push(pageId);
            }
            await updateUserMutation.mutateAsync({
                userId: teamMemberId,
                data: { allowedPages: newPages }
            });
            refetch();
        } catch (error) {
            console.error('Error updating permissions:', error);
            NotifyError('Failed to update permissions', error);
        }
    };

    const startEditQuota = () => {
        const member = memberData?.data?.user;
        setQuotaForm({
            monthlyHours: (member?.meetingRoomQuota?.monthlyHours || 0) / 60,
            dailyHours: (member?.meetingRoomQuota?.meetingRoomHoursPerDay || 0) / 60,
        });
        setIsEditingQuota(true);
    };

    const handleSaveQuota = async () => {
        try {
            await updateQuotaMutation.mutateAsync({
                userId: teamMemberId,
                data: {
                    monthlyHours: parseFloat(quotaForm.monthlyHours) || 0,
                    meetingRoomHoursPerDay: parseFloat(quotaForm.dailyHours) || 0,
                }
            });
            setIsEditingQuota(false);
        } catch (err) {
            NotifyError('Failed to update quota', err);
        }
    };

    if (isLoading) {
        return (
            <div className="flex h-[80vh] items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-brand" />
            </div>
        );
    }

    if (error || !memberData?.data?.user) {
        return (
            <div className="animate-fadeIn px-4 py-8 md:px-[34px]">
                <p className="text-red-600">Failed to load team member details.</p>
                <Link to={URLS.CUSTOMER.TEAM} className="mt-4 inline-block font-semibold text-brand hover:underline">Back to Team</Link>
            </div>
        );
    }

    const member = memberData.data.user;
    const roleLabel = member.role === 'user' ? 'Owner' : 'Member';
    const ownerMonthlyHrs = (userDetails?.meetingRoomQuota?.monthlyHours || 0) / 60;
    const ownerDailyHrs = (userDetails?.meetingRoomQuota?.meetingRoomHoursPerDay || 0) / 60;
    const tmMonthlyHrs = (member?.meetingRoomQuota?.monthlyHours || 0) / 60;
    const tmDailyHrs = (member?.meetingRoomQuota?.meetingRoomHoursPerDay || 0) / 60;
    const tmUsedHrs = (member?.meetingRoomQuota?.usedHours || 0) / 60;

    // A team member's cap can never exceed the organization's own pool.
    const isMonthlyOverMax = isEditingQuota && parseFloat(quotaForm?.monthlyHours) > ownerMonthlyHrs;
    const isDailyOverMax = isEditingQuota && parseFloat(quotaForm?.dailyHours) > ownerDailyHrs;
    const isQuotaFormInvalid = isMonthlyOverMax || isDailyOverMax;

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
            {/* Header */}
            <div className="mb-6 flex items-center gap-4">
                <Link to={URLS.CUSTOMER.TEAM}>
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#ECE3D5] bg-white text-[#6B675F] transition-colors hover:bg-cream-input">
                        <ArrowLeft size={20} />
                    </div>
                </Link>
                <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] bg-cream-input font-display text-[19px] font-bold text-brand">
                    {member.name?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2.5">
                        <h1 className="truncate font-display text-[23px] font-bold text-ink">{member.name}</h1>
                        <span className="shrink-0 rounded-full bg-cream-input px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9F440B]">{roleLabel}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[13px] text-[#8C8175]">
                        <Mail size={16} className="text-[#B7AC9E]" />
                        {member.email}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-[1.2fr_1fr] lg:items-start">
                {/* Access Permissions */}
                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="mb-2 flex items-center gap-3">
                        <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] bg-cream-input text-brand">
                            <Shield size={20} />
                        </div>
                        <h2 className="font-display text-[17px] font-semibold text-ink">Access Permissions</h2>
                    </div>
                    <p className="mb-4 text-[13px] text-[#8C8175]">Control which pages and features this team member can access within the portal.</p>

                    <div className="space-y-3">
                        <PermissionToggle
                            title="Support Tickets"
                            subtitle="Allow user to create and view support tickets"
                            checked={member.allowedPages?.includes('support') || false}
                            onToggle={() => handleTogglePermission('support')}
                            disabled={updateUserMutation.isLoading}
                        />
                        <PermissionToggle
                            title="Contracts"
                            subtitle="Allow user to view your active contracts"
                            checked={member.allowedPages?.includes('contracts') || false}
                            onToggle={() => handleTogglePermission('contracts')}
                            disabled={updateUserMutation.isLoading}
                        />
                    </div>
                </div>

                {/* Meeting Room Allocation */}
                <div className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="mb-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-[38px] w-[38px] items-center justify-center rounded-[11px] bg-cream-input text-brand">
                                <KeyRound size={20} />
                            </div>
                            <h2 className="font-display text-[17px] font-semibold text-ink">Meeting Room Allocation</h2>
                        </div>
                        {!isEditingQuota ? (
                            <button
                                onClick={startEditQuota}
                                className="shrink-0 rounded-[9px] border-[1.5px] border-[#E6DDCF] bg-white px-3.5 py-1.5 font-display text-xs font-semibold text-brand transition-colors hover:bg-cream-input"
                            >
                                Edit
                            </button>
                        ) : (
                            <div className="flex shrink-0 gap-2">
                                <button
                                    onClick={handleSaveQuota}
                                    disabled={updateQuotaMutation.isLoading || isQuotaFormInvalid}
                                    className="flex items-center gap-1.5 rounded-[9px] bg-brand px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
                                >
                                    {updateQuotaMutation.isLoading ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                                    Save
                                </button>
                                <button
                                    onClick={() => setIsEditingQuota(false)}
                                    disabled={updateQuotaMutation.isLoading}
                                    className="rounded-[9px] px-3 py-1.5 text-xs font-semibold text-[#6B675F] transition-colors hover:bg-cream disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>

                    <p className="mb-4 text-[13px] text-[#8C8175]">
                        Set how much of your organization&rsquo;s <strong className="font-semibold text-ink">{ownerMonthlyHrs} hr/month</strong> pool this member can use.
                    </p>

                    {!isEditingQuota ? (
                        <div className="space-y-2.5">
                            <div className="flex items-center justify-between rounded-xl border border-[#C6E3D4] bg-[#E4F1EA] px-4 py-3.5">
                                <div className="flex items-center gap-2.5 text-[#2F7D5E]">
                                    <Clock size={18} />
                                    <span className="font-display text-[13.5px] font-semibold text-ink">Monthly Cap</span>
                                </div>
                                <span className="font-display text-[15px] font-bold text-[#256B50]">
                                    {tmMonthlyHrs > 0 ? `${tmMonthlyHrs} hrs` : <span className="text-sm font-normal text-[#A89C8C]">Not set</span>}
                                </span>
                            </div>
                            <div className="flex items-center justify-between rounded-xl border border-[#F1E9DC] bg-cream px-4 py-3.5">
                                <span className="text-[13.5px] font-semibold text-[#3B2A1E]">Daily Cap</span>
                                <span className="font-display text-[15px] font-bold text-ink">
                                    {tmDailyHrs > 0 ? `${tmDailyHrs} hrs/day` : <span className="text-sm font-normal text-[#A89C8C]">Not set</span>}
                                </span>
                            </div>
                            <div className="flex items-center justify-between rounded-xl border border-[#F1E9DC] bg-cream px-4 py-3.5">
                                <span className="text-[13.5px] font-semibold text-[#3B2A1E]">Used This Month</span>
                                <span className="font-display text-[15px] font-bold text-ink">{tmUsedHrs.toFixed(1)} hrs</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className={`mb-1 block text-sm ${isMonthlyOverMax ? 'font-semibold text-red-600' : 'text-[#6B675F]'}`}>
                                    Monthly Cap (max {ownerMonthlyHrs} hrs from your pool)
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    step={0.5}
                                    value={quotaForm.monthlyHours}
                                    onChange={e => setQuotaForm({ ...quotaForm, monthlyHours: e.target.value })}
                                    className={`w-full rounded-lg border-[1.5px] bg-white px-3 py-2 text-sm outline-none transition-colors ${isMonthlyOverMax ? 'border-red-400 text-red-600 focus:border-red-500' : 'border-cream-200 focus:border-brand'}`}
                                    disabled={updateQuotaMutation.isLoading}
                                    placeholder="e.g. 4"
                                />
                                {isMonthlyOverMax ? (
                                    <p className="mt-1 text-xs font-medium text-red-600">Cannot exceed your organization's {ownerMonthlyHrs} hr/month pool.</p>
                                ) : (
                                    <p className="mt-1 text-xs text-[#A89C8C]">Enter 0 for no personal cap (org pool limit applies).</p>
                                )}
                            </div>
                            <div>
                                <label className={`mb-1 block text-sm ${isDailyOverMax ? 'font-semibold text-red-600' : 'text-[#6B675F]'}`}>
                                    Daily Cap — <span className={isDailyOverMax ? 'text-red-600' : ''}>{quotaForm.dailyHours} hrs/day</span> (max {ownerDailyHrs})
                                </label>
                                <input
                                    type="range"
                                    min={0}
                                    max={ownerDailyHrs || 10}
                                    step={0.5}
                                    value={quotaForm.dailyHours}
                                    onChange={e => setQuotaForm({ ...quotaForm, dailyHours: e.target.value })}
                                    className={`w-full ${isDailyOverMax ? 'accent-red-500' : 'accent-brand'}`}
                                    disabled={updateQuotaMutation.isLoading}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <BottomUXSpace />
        </div>
    );
};

export default CustomerTeamMemberDetail;
